/**
 * Thin wrapper around the frozen coding-exam contract
 * (see TASK_02_coding_assessment.md Step 1). All calls are keyed by the
 * session's access_token, the same value CandidateLanding stores in
 * localStorage after the info endpoint call.
 */
import axios from 'axios';
import { getPublicApiOrigin } from '../config/env';

const base = () => `${getPublicApiOrigin()}/api/v1/public/coding`;

export function getAccessToken() {
  return localStorage.getItem('access_token');
}

export async function fetchQuestion(index) {
  const token = getAccessToken();
  const { data } = await axios.get(`${base()}/${token}/question/${index}`);
  return data;
}

export async function runCode({ questionId, language, code, stdin }) {
  const token = getAccessToken();
  try {
    const { data } = await axios.post(`${base()}/${token}/run`, {
      question_id: questionId,
      language,
      code,
      ...(stdin != null && stdin !== '' ? { stdin } : {}),
    });
    return { ok: true, ...data };
  } catch (err) {
    if (err.response?.status === 429) {
      return { ok: false, rateLimited: true, retryAfterSeconds: err.response.data?.detail?.retry_after_seconds ?? 30 };
    }
    if (err.response?.status === 503) {
      return { ok: false, unavailable: true, message: err.response.data?.detail || 'The code execution service is temporarily unavailable.' };
    }
    if (err.response?.status === 410) {
      return { ok: false, expired: true, message: err.response.data?.detail };
    }
    if (err.response?.status === 409) {
      return { ok: false, alreadyComplete: true, message: err.response.data?.detail || 'This exam has already been completed.' };
    }
    throw err;
  }
}

export async function submitCode({ questionId, language, code }) {
  const token = getAccessToken();
  try {
    const { data } = await axios.post(`${base()}/${token}/submit`, {
      question_id: questionId,
      language,
      code,
    });
    return { ok: true, ...data };
  } catch (err) {
    if (err.response?.status === 503) {
      return { ok: false, unavailable: true, message: err.response.data?.detail || 'The code execution service is temporarily unavailable.' };
    }
    if (err.response?.status === 410) {
      return { ok: false, expired: true, message: err.response.data?.detail };
    }
    if (err.response?.status === 409) {
      return { ok: false, alreadyComplete: true, message: err.response.data?.detail || 'This exam has already been completed.' };
    }
    throw err;
  }
}

export async function saveDraft({ questionId, language, code }) {
  const token = getAccessToken();
  // Best-effort — a failed autosave must never interrupt the candidate or
  // discard their code, which still lives in React state either way.
  try {
    await axios.put(`${base()}/${token}/draft`, {
      question_id: questionId,
      language,
      code,
    });
    return true;
  } catch (err) {
    console.warn('Draft autosave failed (code is not lost, only the save):', err);
    return false;
  }
}

/**
 * End the whole coding exam (any question, including zero submissions).
 * Optionally includes the open editor so that answer is scored before finish.
 */
export async function submitExam({ questionId, language, code } = {}) {
  const token = getAccessToken();
  const body = {};
  if (questionId != null && language != null && code != null) {
    body.question_id = questionId;
    body.language = language;
    body.code = code;
  }
  try {
    const { data } = await axios.post(`${base()}/${token}/finish`, body);
    return { ok: true, ...data };
  } catch (err) {
    if (err.response?.status === 410) {
      return { ok: false, expired: true, message: err.response.data?.detail };
    }
    if (err.response?.status === 409) {
      return { ok: false, alreadyComplete: true, message: err.response.data?.detail || 'This exam has already been completed.' };
    }
    throw err;
  }
}
